import React, { useEffect, useState } from 'react';
import { BorderedInput, Button, Drawer, Typography } from '@navi/web-ui/lib/primitives';
import { formatAmount } from '@cp/src/utils/commonUtils';
import { Due } from '../../interfaces/CaseDetail.type';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import FeeWaiveInfoBanner from './FeeWaiveInfoBanner';
import EmiBreakup from './EmiBreakup';
import { useForm } from 'react-hook-form';
import { IFeeWaiveSubmitPayload, WaiveFeeFormFieldsV2 } from '../../interfaces/EmiSchedule.type';
import AdjustEmiView from './AdjustEmiView';
import FeeWaiverActionButtons from './FeeWaiverActionButtons';
import styles from './index.module.scss';
import { submitFeeWaiveV2 } from '../../actions/EmiScheduleActions';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getCustomerDetails } from '../../actions/casesDetailsActions';
import Loader from '@cp/src/components/Loader/Loader';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { FEE_WAIVER_CLICKSTREAM } from '@cp/src/service/clickStream.constant';
import { EmiFeeActionLabelMap, EmiFeeActions } from './interfaces';

export interface IWaiveFeeDrawerV2 {
  emiItem: Due;
  isOpen: boolean;
  closeDrawer: () => void;
}

const WaiveFeeDrawerV2: React.FC<IWaiveFeeDrawerV2> = ({ emiItem, isOpen, closeDrawer }) => {
  const {
    emiNumber,
    unpaidDueAmount,
    additionalWaiverInfo,
    referenceId,
    action,
    isMaxWaived,
    waivedAmount,
    isColendingFlow
  } = emiItem || {};

  const {
    loanId = '',
    customerId = '',
    caseBusinessVertical = '',
    caseReferenceId = ''
  } = useParams();

  const { maxWaivableAmount } = additionalWaiverInfo || {};
  const {
    register,
    handleSubmit,
    watch,
    resetField,
    formState: { errors, isValid }
  } = useForm<WaiveFeeFormFieldsV2>({
    mode: 'onChange'
  });

  const waivedValue = watch(`componentAdjustments.${referenceId}.waiverAmount`);
  const [showAdjustmentView, setShowAdjustmentView] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // prevent special characters including decimal point
    if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
      e.preventDefault();
    }
  };

  const submitFeeWaiver = async (waivedAmount: number) => {
    const payload: IFeeWaiveSubmitPayload = {
      dueReferenceId: referenceId,
      waiverAmount: waivedAmount
    };
    setIsSubmitting(true);
    try {
      await submitFeeWaiveV2(loanId, payload);
      toast(
        `${formatAmount(waivedAmount)} ${
          waivedAmount >= unpaidDueAmount ? 'waived' : 'held'
        } for EMI ${emiNumber}`,
        { type: 'info' }
      );
      addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_WAIVE_SUCCESSFULLY_DONE, {
        referenceId: referenceId,
        loanAccountNumber: loanId,
        amount: waivedAmount,
        action: Number(waivedAmount) >= unpaidDueAmount ? 'Waive' : 'Hold'
      });
      closeDrawer();
      dispatch(getCustomerDetails(loanId, customerId, caseReferenceId, caseBusinessVertical));
    } catch (error) {
      toast(error as string, { type: 'error' });
    }
    setIsSubmitting(false);
  };

  const handleFeeWaiver = (data: WaiveFeeFormFieldsV2) => {
    if (!isValid) {
      return;
    }
    const waivedAmount = Number(data?.componentAdjustments?.[referenceId]?.waiverAmount);
    addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_WAIVE_SUBMIT_CLICKED, {
      referenceId: referenceId,
      loanAccountNumber: loanId,
      amount: waivedAmount,
      action: Number(waivedAmount) >= unpaidDueAmount ? 'Waive' : 'Hold'
    });
    if (waivedAmount && waivedAmount > unpaidDueAmount) {
      setShowAdjustmentView(true);
      return;
    }
    submitFeeWaiver(waivedAmount);
  };

  useEffect(() => {
    if (isOpen) {
      addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_WAIVE_DRAWER_LOADED, {
        referenceId: referenceId,
        loanAccountNumber: loanId
      });
    }
    return () => {
      resetField(`componentAdjustments.${referenceId}.waiverAmount`);
      setShowAdjustmentView(false);
    };
  }, [isOpen]);

  useEffect(() => {
    if (showAdjustmentView) {
      addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_WAIVE_PROMPT_LOADED, {
        referenceId: referenceId,
        loanAccountNumber: loanId,
        waivedInstantlyAmount: unpaidDueAmount,
        adjustmentAmount: waivedValue - unpaidDueAmount
      });
    }
  }, [showAdjustmentView]);

  const toggleAdjustmentView = () => {
    setShowAdjustmentView(!showAdjustmentView);
  };

  const handleConfirmAdjustment = (data: WaiveFeeFormFieldsV2) => {
    const waivedAmount = data?.componentAdjustments?.[referenceId]?.waiverAmount;
    addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_WAIVE_CONFIRM_CLICKED, {
      referenceId: referenceId,
      loanAccountNumber: loanId,
      amount: waivedAmount,
      action: Number(waivedAmount) >= unpaidDueAmount ? 'Waive' : 'Hold'
    });
    submitFeeWaiver(waivedAmount);
  };

  const errorMsg = errors.componentAdjustments?.[referenceId]?.waiverAmount?.message;

  return (
    <Drawer
      id="waive-fee-drawer"
      open={isOpen}
      onClose={closeDrawer}
      headerText={`${
        waivedAmount > 0 ? 'Waive' : EmiFeeActionLabelMap[action || EmiFeeActions.HOLD]
      } penalty charges`}
      headerContainerClasses={styles.borderBottom}
      footer={
        <FeeWaiverActionButtons
          showAdjustmentView={showAdjustmentView}
          waivedValue={waivedValue}
          emiItem={emiItem}
          disabled={!isValid || isSubmitting}
          onWaivePenalty={handleSubmit(handleFeeWaiver)}
          onConfirmAdjustment={handleSubmit(handleConfirmAdjustment)}
          onBack={toggleAdjustmentView}
        />
      }
    >
      {showAdjustmentView ? (
        <AdjustEmiView
          waivedAmount={waivedValue}
          totalUnpaidAmount={unpaidDueAmount}
          isColendingFlow={isColendingFlow}
        />
      ) : (
        <div key={referenceId}>
          <Typography variant="h5" className="text-xs mt-4">
            {`${
              waivedAmount > 0 ? 'Waive' : EmiFeeActionLabelMap[action || EmiFeeActions.HOLD]
            } details`}
          </Typography>
          <div className="mt-2">
            <FeeWaiveInfoBanner emiData={emiItem} />
          </div>
          {!isMaxWaived ? (
            <div className="mt-3">
              <Typography variant="p5" className="text-[var(--grayscale-2)]">
                {`${EmiFeeActionLabelMap[action || EmiFeeActions.HOLD]} amount`}
              </Typography>
              <BorderedInput
                type="number"
                {...register(`componentAdjustments.${referenceId}.waiverAmount`, {
                  valueAsNumber: true,
                  min: 1,
                  max: {
                    value: maxWaivableAmount,
                    message: `Enter amount upto ${formatAmount(maxWaivableAmount)}`
                  },
                  required: true
                })}
                placeholder={'Enter amount'}
                LeftInputAdornment={'₹'}
                fullWidth
                onKeyDown={handleKeyDown}
                value={waivedValue}
                error={errorMsg}
              />
              <div className="h-0.25 bg-border mt-6" />
            </div>
          ) : null}
          <Typography variant="h5" className="text-xs mb-2 mt-6">
            EMI {emiNumber} breakup
          </Typography>
          <div className="shadow-base rounded-lg p-3">
            <EmiBreakup emiData={emiItem} />
          </div>
        </div>
      )}
      <Loader show={Boolean(isSubmitting)} className={`overlay white`} animate={false} />
    </Drawer>
  );
};

export default WaiveFeeDrawerV2;
